import React, { useState, useMemo, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  TextField,
  Box,
  Checkbox,
  ListItemText,
  Button,
  useTheme,
  Chip,
} from '@mui/material';
import { useDropdown } from '../../hooks/useDropdown';
import { OptionType } from '../context/FilterContext';

interface AutoCompleteProps {
  label?: string;
  value: string | number | string[] | null;
  onSelect: (value: any | null) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string | null;
  mandatory?: boolean;
  name: OptionType;
  fieldError?: boolean;
  disableIfHasOneValue?: boolean;
  options?: string[];
  isLabelRequired?: boolean;
  selectFirstByDefault?: boolean;
  required?: boolean;
}

export const AutoComplete: React.FC<AutoCompleteProps> = ({
  label = 'Select Option',
  value,
  onSelect,
  disabled = false,
  error,
  helperText,
  name,
  mandatory,
  fieldError,
  disableIfHasOneValue,
  options: defaultOptions,
  isLabelRequired,
  selectFirstByDefault = false,
  required = false,
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  const { loading, error: hookError, getOptions } = useDropdown();

  const getDisplayText = (option: any) => {
    if (!option) return '';
    if (name === 'programme') return option.description || '';
    if (name === 'site') return option.description || '';
    return (
      option?.contractor_area_text ||
      option?.activity ||
      option?.description ||
      option?.authority_name ||
      option?.display_name || 
      option?.area_text ||
      option?.template_name ||
      ''
    );
  };

  const options: any[] = (
    defaultOptions ? defaultOptions : getOptions(name) || []
  ).sort((a: any, b: any) => {
    const hasSeqA = typeof a.sequence_order === "number";
    const hasSeqB = typeof b.sequence_order === "number";
    if (hasSeqA && hasSeqB) {
      return a.sequence_order - b.sequence_order;
    }
    if (hasSeqA && !hasSeqB) return -1;
    if (!hasSeqA && hasSeqB) return 1;
    const textA = getDisplayText(a).toLowerCase();
    const textB = getDisplayText(b).toLowerCase();
    return textA.localeCompare(textB);
  });

  const mergedError = error || !!hookError[name];
  const mergedHelperText = helperText || hookError[name] || '';

  const isMultiSelect = Array.isArray(value);

  const getValue = (option: any) => {
    if (!option) return '';
    return isLabelRequired ? option.description : option.id ?? '';
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option: any) =>
      getDisplayText(option).toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm, name]);

  const handleClearAll = () => {
    onSelect([]);
  };

  const handleSelectAll = () => {
    const allValues = filteredOptions.map((option: any) => getValue(option));
    onSelect(allValues);
  };

  const isAllSelected =
    isMultiSelect &&
    Array.isArray(value) &&
    filteredOptions.length > 0 &&
    filteredOptions.every((option: any) => value.includes(getValue(option)));

  // Auto-select first option if enabled and no value is selected
  useEffect(() => {
    if (selectFirstByDefault && !disabled && !loading && options.length > 0 && (value === null || value === '' || (Array.isArray(value) && value.length === 0))) {
      const firstOption = options[0];
      if (firstOption) {
        const firstValue = getValue(firstOption);
        onSelect(isMultiSelect ? [firstValue] : firstValue);
      }
    }
  }, [selectFirstByDefault, options, value, disabled, loading, isMultiSelect]);


  const validValue = useMemo(() => {
    if (isMultiSelect) {
      return Array.isArray(value) ? value.filter((val) => options.some((opt) => getValue(opt) === val)) : [];
    }
    return options.some((opt) => getValue(opt) === value) ? value : '';
  }, [value, options, isMultiSelect]);

  return (
    <FormControl fullWidth variant="outlined" error={mergedError || fieldError} required={required || mandatory}>
      <InputLabel>
        {label}
      </InputLabel>
      <Select
        multiple={isMultiSelect}
        value={validValue} // Use the validated value
        label={label}
        disabled={disableIfHasOneValue ? options.length == 1 ? true : disabled : disabled}
        onChange={(event) => onSelect(event.target.value)}
        renderValue={(selected) => {
          if (!isMultiSelect) {
            const selectedOption = options.find(
              (opt: any) => getValue(opt) === selected,
            );
            return selectedOption ? getDisplayText(selectedOption) : '';
          }
          if (Array.isArray(selected) && selected.length === 0) {
            return 'None selected';
          }
          if (Array.isArray(selected)) {
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const option = options.find((opt: any) => getValue(opt) === value);
                  const label = option ? getDisplayText(option) : '';
                  return (
                    <Chip
                      key={value}
                      label={label}
                      size="small"
                      onMouseDown={(e) => e.stopPropagation()}
                      onDelete={() => {
                        const newValues = selected.filter((v) => v !== value);
                        onSelect(newValues);
                      }}
                    />
                  );
                })}
              </Box>
            );
          }
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 400,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              '& .MuiMenu-list': {
                padding: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                minHeight: 0,
              },
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            pt: 2,
            pb: 1,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <TextField
            placeholder="Search..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              width: '100%',
              mb: isMultiSelect && filteredOptions.length > 0 ? 1 : 0,
            }}
          />

          {isMultiSelect && filteredOptions.length > 0 && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
                disabled={isAllSelected}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
                disabled={!Array.isArray(value) || value.length === 0}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Clear All
              </Button>
            </Box>
          )}
        </Box>

        {loading ? (
          <MenuItem disabled sx={{ flex: 1 }}>
            <CircularProgress size={20} />
          </MenuItem>
        ) : (
          filteredOptions.map(
            (opt: { id: React.Key | null | undefined }, index: number) => (
              <MenuItem
                key={opt.id}
                value={getValue(opt)}
                sx={{
                  ...(index === 0 && { flexGrow: 1 }),
                  minHeight: '48px',
                }}
              >
                {isMultiSelect && (
                  <Checkbox
                    checked={
                      Array.isArray(value) && value.includes(getValue(opt))
                    }
                    size="small"
                  />
                )}
                <ListItemText
                  primary={getDisplayText(opt)}
                  sx={{ ml: isMultiSelect ? 1 : 0 }}
                />
              </MenuItem>
            ),
          )
        )}

        {!loading && filteredOptions.length === 0 && (
          <MenuItem disabled sx={{ flex: 1, justifyContent: 'center' }}>
            <em>No options available</em>
          </MenuItem>
        )}

        {isMultiSelect && (
          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: theme.palette.background.paper,
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
            }}
          >
            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {Array.isArray(value) ? value.length : 0} of {options.length}{' '}
              selected
            </Box>
          </Box>
        )}
      </Select>
      {mergedHelperText && <FormHelperText>{mergedHelperText}</FormHelperText>}
    </FormControl >
  );
};
